/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik'
import styled from 'styled-components'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm, Theme } from '@nexigendigital/nxui'
import { useUpdateCartItemsMutation } from 'api/shop'
import { addContactToDomain } from 'store/slices/shopSlice'
import { Address } from 'models/account'
import { CartItem } from 'models/shop'
import { getDomainName, getStatesByCountryCode, regex, removeUndefinedNullEmptyStringFromObject } from 'helpers/utils'
import { useAppDispatch, useAppSelector } from 'store/hooks'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    domain: any,
    form: RefObject<FormikProps<Address>>,
    prefillValues: Address,
    mode: 'adding' | 'editing',
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ContactDetailsForm({ domain, form, prefillValues, mode, close }: Props) {
    const dispatch = useAppDispatch()
    const { pathname } = useLocation()
    const { cart, cartItem, cartUpdateStatus, searchTerm } = useAppSelector((state) => state.shop)
    const [updateCartItems] = useUpdateCartItemsMutation()
    const { name: tld } = domain
    const domainName = `${getDomainName(searchTerm)}${tld}`

    function UpdateState() {
        const { values, initialValues, setFieldValue } = useFormikContext<{ country: string, state: string }>()

        useEffect(() => {
            if (values.country !== initialValues.country && values.state === initialValues.state) {
                setFieldValue('state', getStatesByCountryCode(values.country)[0].state_code)
            }
        }, [values, initialValues, setFieldValue, form.current])

        return null
    }

    function getIntlCode(country: string) {
        return country === 'AU' ? ['+61'] : ['+64']
    }

    useEffect(() => {
        if (cartUpdateStatus === 'success' && pathname === '/checkout') {
            close()
        }
    }, [cartUpdateStatus, pathname])

    return (
        <Formik
            innerRef={form}
            initialValues={prefillValues}
            validationSchema={Yup.object({
                firstname: Yup.string()
                    .required('Required'),
                lastname: Yup.string()
                    .required('Required'),
                phone: Yup.string()
                    .required('Required')
                    .matches(regex.phone, 'Invalid phone number'),
                email: Yup.string()
                    .required('Required')
                    .email('Invalid Email address'),
                address1: Yup.string()
                    .required('Required'),
                suburb: Yup.string()
                    .required('Required'),
                postcode: Yup.number()
                    .typeError('Invalid postcode')
                    .required('Required')
                    .min(1000, 'Invalid postcode')
                    .max(9999, 'Invalid postcode')
            })}
            onSubmit={(values) => {
                if (cart?.uuid) {
                    const contact = removeUndefinedNullEmptyStringFromObject(values)
                    const updatedItems = cart.items.map((item: CartItem) => {
                        if (mode === 'editing' && item.product_id === cartItem.item.product_id && item.name === cartItem.item.name) {
                            return {
                                ...item,
                                domain_contact_fields: contact
                            }
                        }

                        if (mode === 'adding' && item.product_id === domain.id && item.name === domainName) {
                            return {
                                ...item,
                                domain_contact_fields: contact
                            }
                        }

                        return item
                    })
                    updateCartItems({ uuid: cart.uuid, items: updatedItems })
                    dispatch(addContactToDomain({
                        product_id: domain.id ? domain.id : domain.product_id,
                        name: domainName,
                        contact
                    }))

                    if (mode === 'adding') {
                        close()
                    }
                } else {
                    const contact = removeUndefinedNullEmptyStringFromObject(values)
                    dispatch(addContactToDomain({ product_id: domain.id, name: domainName, contact }))
                    close()
                }
            }}
        >
            {({ isSubmitting, values }) => (
                <Form id={'contactDetailsForm'}>
                    <Row>
                        <Field
                            label={'First Name'}
                            name={'firstname'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                        <Field
                            label={'Last Name'}
                            name={'lastname'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                    </Row>
                    <Field
                        label={'Phone Number'}
                        name={'phone'}
                        type={'text'}
                        prefixes={getIntlCode(values.country)}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        label={'Email Address'}
                        name={'email'}
                        type={'text'}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        label={'Company Name'}
                        name={'company'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        label={'Address 1'}
                        name={'address1'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Field
                        label={'Address 2'}
                        name={'address2'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Row>
                        <Field
                            label={'Suburb'}
                            name={'suburb'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                        <Field
                            label={'Postcode'}
                            name={'postcode'}
                            type={'text'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                    </Row>
                    <Row>
                        <Field
                            label={'State'}
                            name={'state'}
                            type={'select'}
                            disabled={isSubmitting}
                            list={getStatesByCountryCode(values.country).map(({ state_code, state }) => (
                                { label: state, value: state_code }
                            ))}
                            component={NXUIForm.SelectField}
                        />
                        <Field
                            label={'Country'}
                            name={'country'}
                            type={'select'}
                            disabled={isSubmitting}
                            list={[
                                { value: 'AU', label: 'Australia' },
                                { value: 'NZ', label: 'New Zealand' }
                            ]}
                            component={NXUIForm.SelectField}
                        />
                    </Row>
                    <UpdateState/>
                </Form>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const Row = styled.div`
    display: flex;
    justify-content: space-between;
    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
        flex-direction: column;
    }

    > div {
        width: 49%;
        ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
            width: 100%;
        }
    }
`
